import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

// ============ INTERFACES ============

interface HistoricalCharacter {
  id: string;
  name: string;
  level: number;
  reputation: number;
  berries: number;
  shipType: string;
  characterType: string;
  lastPlayedAt: string;
  isOnline: boolean;
  infamy?: number; // Only for online players
  isAlive?: boolean; // For death tracking
  deathCount?: number;
  killCount?: number;
}

interface WorldHistoryPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
}

// ============ HELPER FUNCTIONS ============

const getReputationTier = (reputation: number): { label: string; color: string } => {
  if (reputation >= 10000) return { label: 'Legendary', color: '#e74c3c' };
  if (reputation >= 5000) return { label: 'Infamous', color: '#9b59b6' };
  if (reputation >= 2000) return { label: 'Notorious', color: '#f39c12' };
  if (reputation >= 500) return { label: 'Known', color: '#3498db' };
  if (reputation >= 100) return { label: 'Rising', color: '#2ecc71' };
  return { label: 'Rookie', color: '#95a5a6' };
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// const getShipIcon = (shipType: string): string => {
//   switch (shipType) {
//     case 'sloop': return 'S';
//     case 'brigantine': return 'B';
//     case 'galleon': return 'G';
//     case 'man-o-war': return 'M';
//     default: return '-';
//   }
// };

// ============ SKELETON COMPONENTS ============

const CharacterSkeleton: React.FC = () => (
  <div className="history-character skeleton">
    <div className="character-rank skeleton-box" style={{ width: 24, height: 24 }} />
    <div className="character-info">
      <div className="skeleton-box" style={{ width: 120, height: 16, marginBottom: 4 }} />
      <div className="skeleton-box" style={{ width: 80, height: 12 }} />
    </div>
    <div className="character-stats">
      <div className="skeleton-box" style={{ width: 60, height: 14 }} />
    </div>
  </div>
);

// ============ MAIN COMPONENT ============

export const WorldHistoryPanel: React.FC<WorldHistoryPanelProps> = ({ socket, isOpen, onClose }) => {
  const [characters, setCharacters] = useState<HistoricalCharacter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'legends' | 'online' | 'recent'>('online');
  const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);

  const fetchWorldHistory = useCallback(() => {
    if (!socket) {
      // No socket - show empty state after brief delay
      setTimeout(() => {
        setLoading(false);
        setHasTimedOut(true);
      }, 500);
      return;
    }
    
    setLoading(true);
    setHasTimedOut(false);
    socket.emit('world:getHistory', { tab: activeTab });
    
    // Timeout after 5 seconds if no response
    const timeout = setTimeout(() => {
      setLoading(false);
      setHasTimedOut(true);
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [socket, activeTab]);

  useEffect(() => {
    if (!socket || !isOpen) return;

    const handleHistoryData = (data: { characters: HistoricalCharacter[] }) => {
      setCharacters(data.characters || []);
      setLoading(false);
      setHasTimedOut(false);
    };

    socket.on('world:historyData', handleHistoryData);
    fetchWorldHistory();

    return () => {
      socket.off('world:historyData', handleHistoryData);
    };
  }, [socket, isOpen, fetchWorldHistory]);

  useEffect(() => {
    if (isOpen) {
      fetchWorldHistory();
    }
  }, [activeTab, isOpen, fetchWorldHistory]);

  if (!isOpen) return null;

  const sortedCharacters = [...characters].sort((a, b) => {
    // Online players first, then by reputation/infamy
    if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
    const aScore = (a.infamy || 0) + a.reputation + (a.level * 100);
    const bScore = (b.infamy || 0) + b.reputation + (b.level * 100);
    return bScore - aScore;
  });

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="world-history-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="panel-header">
          <h2 className="panel-title">World History</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="history-tabs">
          <button 
            className={`tab-btn ${activeTab === 'legends' ? 'active' : ''}`}
            onClick={() => setActiveTab('legends')}
          >
            Legends
          </button>
          <button 
            className={`tab-btn ${activeTab === 'online' ? 'active' : ''}`}
            onClick={() => setActiveTab('online')}
          >
            Online
          </button>
          <button 
            className={`tab-btn ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
          </button>
        </div>

        {/* Content */}
        <div className="history-content">
          {loading ? (
            <div className="history-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <CharacterSkeleton key={i} />
              ))}
            </div>
          ) : sortedCharacters.length === 0 ? (
            <div className="empty-state">
              <p>{hasTimedOut ? 'Could not load data' : 'No legends have emerged yet...'}</p>
              <p className="empty-hint">
                {hasTimedOut ? 'Check your connection and try again' : 'Be the first to make history!'}
              </p>
              {hasTimedOut && (
                <button 
                  onClick={() => fetchWorldHistory()}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    background: '#238636',
                    border: '1px solid #2ea043',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          ) : (
            <div className="history-list">
              {sortedCharacters.map((char, index) => {
                const tier = getReputationTier(char.reputation + (char.infamy || 0));
                return (
                  <div 
                    key={char.id} 
                    className={`history-character ${char.isOnline ? 'online' : ''}`}
                  >
                    {/* Rank */}
                    <div className="character-rank">
                      {index < 3 ? (
                        <span className={`rank-medal rank-${index + 1}`}>
                          {index === 0 ? '#1' : index === 1 ? '#2' : '#3'}
                        </span>
                      ) : (
                        <span className="rank-number">#{index + 1}</span>
                      )}
                    </div>

                    {/* Character Info */}
                    <div className="character-info">
                      <div className="character-name-row">
                        {/* <span className="ship-icon">{getShipIcon(char.shipType)}</span> */}
                        <span className="character-name">{char.name}</span>
                        {char.isOnline && <span className="online-dot" />}
                      </div>
                      <div className="character-meta">
                        <span 
                          className="reputation-badge"
                          style={{ backgroundColor: tier.color }}
                        >
                          {tier.label}
                        </span>
                        <span className="level-badge">Lv.{char.level}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="character-stats">
                      {char.infamy !== undefined && char.infamy > 0 && (
                        <div className="stat infamy">
                          <span className="stat-icon">[INFAMY]</span>
                          <span className="stat-value">{char.infamy.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="stat berries">
                        <span className="stat-icon">₱</span>
                        <span className="stat-value">{char.berries.toLocaleString()}</span>
                      </div>
                      {!char.isOnline && (
                        <div className="last-seen">
                          {formatTimeAgo(char.lastPlayedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="panel-footer">
          <span className="footer-text">
            {sortedCharacters.filter(c => c.isOnline).length} pirates online
          </span>
        </div>
      </div>
    </div>
  );
};
